import React, { useState } from "react";
import { toast } from "sonner";
import { Watch, Heart, Activity, Moon, Footprints, Thermometer, Check, Plus } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { LineChart } from "../components/MiniChart";
import { WEARABLE_DEVICES, WEARABLE_DATA, loadWearableConn, saveWearableConn, DAYS } from "../phase2";

const metricIcons = { restingHR: Heart, hrv: Activity, sleep: Moon, steps: Footprints, skinTemp: Thermometer };

export default function Wearables() {
  const [conn, setConn] = useState(loadWearableConn());

  const toggle = (id, name) => {
    const next = { ...conn, [id]: !conn[id] };
    setConn(next); saveWearableConn(next);
    toast[next[id] ? "success" : "message"](`${name} ${next[id] ? "connected — syncing data" : "disconnected"}`);
  };

  const anyConnected = Object.values(conn).some(Boolean);
  const metrics = Object.values(WEARABLE_DATA);

  return (
    <AppLayout title="Wearables">
      <h3 className="font-head font-semibold text-slate-800 mb-3">Connect your devices</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {WEARABLE_DEVICES.map((d) => {
          const on = !!conn[d.id];
          return (
            <div key={d.id} className={`rounded-xl border p-5 text-center transition-colors ${on ? "border-[#0d9488] bg-[#e6f4f2]" : "border-slate-200 bg-white"}`}>
              <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mx-auto"><Watch size={24} className={on ? "text-[#0d9488]" : "text-slate-400"} /></div>
              <p className="font-semibold text-slate-800 mt-3">{d.name}</p>
              <button onClick={() => toggle(d.id, d.name)} className={`mt-3 w-full h-9 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${on ? "bg-[#0d9488] text-white hover:bg-[#0f766e]" : "border border-slate-200 text-slate-600 hover:border-[#0d9488]"}`}>
                {on ? <><Check size={15} /> Connected</> : <><Plus size={15} /> Connect</>}
              </button>
            </div>
          );
        })}
      </div>

      <h3 className="font-head font-semibold text-slate-800 mt-8 mb-3">Synced health metrics</h3>
      {!anyConnected ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
          <Watch size={34} className="text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">Connect a device above to sync your heart rate, HRV, sleep, steps and skin temperature.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {metrics.map((m) => {
            const Icon = metricIcons[m.key] || Activity;
            const bad = m.higherIsWorse ? m.today > m.threshold : m.today < m.threshold;
            return (
              <div key={m.key} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><Icon size={18} /></div><span className="text-[13px] font-medium text-slate-500">{m.label}</span></div>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${bad ? "bg-[#fdeceb] text-[#e0567a]" : "bg-[#eafaf1] text-[#22a35a]"}`}>{bad ? "Watch" : "Normal"}</span>
                </div>
                <p className="text-2xl font-head font-bold text-slate-800 mt-2">{m.today.toLocaleString()}<span className="text-sm font-normal text-slate-400 ml-1">{m.unit}</span></p>
                <p className="text-[11px] text-slate-400 mb-2">Normal: {m.normal}</p>
                <LineChart data={m.series} color={bad ? "#e0567a" : "#0d9488"} height={44} />
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
